<template>
    <b-modal
            id="record-catch-exif-modal"
            ref="record-catch-exif-modal"
            hide-footer
            centered
            size="xl"
            title="Kép EXIF adatok"
    >
        <b-row>
            <b-col cols="12" md="8">
                <img class="img-fluid" :src="imageUrl" alt="" title="" />
            </b-col>
            <b-col cols="12" md="4">
                <div v-if="summary" class="mb-3">
                        <p v-if="summary.takenAt"><feather-icon icon="CalendarIcon" class="mr-1" /><strong>Készítés ideje:</strong> {{ summary.takenAt }}</p>
                        <p v-if="summary.device"><feather-icon icon="SmartphoneIcon" class="mr-1" /><strong>Eszköz:</strong> {{ summary.device }}</p>
                        <p v-if="summary.focal"><feather-icon icon="ZoomInIcon" class="mr-1" /><strong>Fókusztáv:</strong> {{ summary.focal }}</p>
                        <p v-if="summary.fNumber"><feather-icon icon="ApertureIcon" class="mr-1" /><strong>Blende:</strong> f/{{ summary.fNumber }}</p>
                        <p v-if="summary.exposure"><feather-icon icon="ClockIcon" class="mr-1" /><strong>Záridő:</strong> {{ summary.exposure }}</p>
                        <p v-if="summary.iso"><feather-icon icon="FilmIcon" class="mr-1" /><strong>ISO:</strong> {{ summary.iso }}</p>
                        <p v-if="summary.filmEq"><feather-icon icon="SquareIcon" class="mr-1" /><strong>Film eq.:</strong> {{ summary.filmEq }}</p>
                        <p v-if="summary.gps">
                            <feather-icon icon="MapPinIcon" class="mr-1" /><strong>Hely:</strong>
                            <a :href="summary.gps.url" target="_blank" rel="noopener">
                                Google Maps megnyitása
                            </a>
                            <span class="text-muted"> ({{ summary.gps.lat.toFixed(6) }}, {{ summary.gps.lon.toFixed(6) }})</span>
                        </p>

                        <div v-if="summary.gps" style="aspect-ratio: 16/9; border-radius: 8px; overflow: hidden; border: 1px solid #eee;">
                            <iframe
                                    :src="`https://maps.google.com/maps?q=${summary.gps.lat},${summary.gps.lon}&z=15&output=embed`"
                                    width="100%" height="100%" frameborder="0" style="border:0;" allowfullscreen
                                    referrerpolicy="no-referrer-when-downgrade"
                            ></iframe>
                        </div>
                        <div>
                            <json-viewer
                                    :value="exif"
                                    :expand-depth=1
                                    :copyable="{copyText: 'Másolás', copiedText: 'Másolva', timeout: 2000}"
                                    sort></json-viewer>
                        </div>
                </div>
            </b-col>
        </b-row>
    </b-modal>
</template>

<script>

import {BOverlay, BCol, BRow} from 'bootstrap-vue';
import JsonViewer from 'vue-json-viewer';

export default {
    props: {
        exif: {
            required: true
        },
        imageUrl: {
            required: true
        }
    },
    components: {
        BOverlay,
        BCol,
        BRow,
        JsonViewer
    },
    computed: {
        summary() {
            try {
                const e = this.exif || {}
                const EXIF = e.EXIF || {}
                const IFD0 = e.IFD0 || {}
                const GPS  = e.GPS  || {}
                const CMP  = e.COMPUTED || {}

                // helpers
                const r2f = (val) => {
                    // "17/4" | ["17/4"] | ["17/4","1/1"] | 4.25 |  [807365/524263, ...] (néha szám is)
                    const asStr = v => (typeof v === 'string' ? v : (Array.isArray(v) ? v[0] : v))
                    const v = asStr(val)
                    if (typeof v === 'number') return v
                    if (typeof v === 'string' && v.includes('/')) {
                        const [n, d] = v.split('/').map(Number)
                        if (!isNaN(n) && !isNaN(d) && d !== 0) return n / d
                    }
                    if (typeof v === 'string') {
                        const x = Number(v)
                        if (!isNaN(x)) return x
                    }
                    return null
                }

                const formatFNumber = (val) => {
                    const f = r2f(val)
                    return f ? f.toFixed(1).replace(/\.0$/, '') : null
                }

                const formatFocal = (val, unit='mm') => {
                    const f = r2f(val)
                    return f ? `${f.toFixed(2).replace(/\.00$/, '')} ${unit}` : null
                }

                const formatExposure = (val) => {
                    // bármiből ( "1/4608" | "14997117/500000000" | 0.03 | 1/125 ) -> "1/N s"
                    const toSeconds = (v) => {
                        if (v == null) return null
                        // ha string arány
                        if (typeof v === 'string' && v.includes('/')) {
                            const [n, d] = v.split('/').map(Number)
                            if (!isNaN(n) && !isNaN(d) && d !== 0) return n / d
                            return null
                        }
                        // ha szám (pl. 0.03)
                        const f = Number(v)
                        return isNaN(f) ? null : f
                    }

                    const s =
                        toSeconds(val) ??
                        // ha valamilyen objektum érkezne vagy más mező, próbáljuk stringgé erőltetni
                        (typeof val === 'object' ? toSeconds(String(val)) : null)

                    if (s == null || s <= 0) return null

                    // invertálás és kerekítés
                    const N = Math.round(1 / s)

                    // széljegyzet: ha hosszú expó (>= 1s), akkor is 1/N-t kérted;
                    // így 1/1 s, 1/2 s stb. formában jelenítjük meg.
                    return `1/${N} s`
                }

                const dmsToDec = (arr, ref) => {
                    if (!arr || !arr.length) return null
                    const d = r2f(arr[0]), m = r2f(arr[1]), s = r2f(arr[2])
                    if (d == null || m == null || s == null) return null
                    let dec = d + (m / 60) + (s / 3600)
                    if (ref === 'S' || ref === 'W') dec *= -1
                    return dec
                }

                const lat = dmsToDec(GPS.GPSLatitude, GPS.GPSLatitudeRef)
                const lon = dmsToDec(GPS.GPSLongitude, GPS.GPSLongitudeRef)
                const tz  = EXIF['UndefinedTag:0x9011'] || EXIF['UndefinedTag:0x9010'] || ''

                // some sources adj. datetime lives in EXIF.DateTimeOriginal
                const dt = EXIF.DateTimeOriginal || IFD0.DateTime || null
                const takenAt = dt ? `${dt.replace(/:/, '-').replace(/:/, '-')} ${tz}`.trim() : null

                return {
                    takenAt,
                    device: [IFD0.Make, IFD0.Model].filter(Boolean).join(' ').trim() || null,
                    focal: formatFocal(EXIF.FocalLength || (EXIF['UndefinedTag:0xA432'] && EXIF['UndefinedTag:0xA432'][1])),
                    fNumber: formatFNumber(EXIF.FNumber || EXIF['UndefinedTag:0xA432']?.[2] || CMP?.ApertureFNumber?.replace('f/','')),
                    exposure: formatExposure(EXIF.ExposureTime || EXIF.ShutterSpeedValue),
                    iso: EXIF.ISOSpeedRatings || EXIF.PhotographicSensitivity || null,
                    filmEq: EXIF.FocalLengthIn35mmFilm ? `${EXIF.FocalLengthIn35mmFilm} mm` : null,
                    gps: (lat != null && lon != null) ? {
                        lat, lon,
                        url: `https://www.google.com/maps?q=${lat},${lon}`
                    } : null
                }
            } catch (err) {
                console.error('EXIF parse error', err)
                return null
            }
        }
    }
}
</script>
